// Feature spec 17, phase 3: what the entry point list, form, Overview band and deployment side share.
import type {
  EdgeKind,
  EntryPointRouteStatus,
  EntryPointSettings,
  NetworkKinds,
  OriginAccessMode,
} from '../../api/types';
import { strings } from '../../strings';

const e = strings.networks.entryPoints;

/** Strongest first (spec 11 4.10). */
export const ACCESS_ORDER: OriginAccessMode[] = [
  'private-link',
  'private-endpoint',
  'vpc-origin',
  'serverless-neg',
  'access-restriction',
  'allow-list-and-header',
  'header',
  'none',
];

export const ACCESS_STRENGTH: Record<OriginAccessMode, 'strongest' | 'strong' | 'medium' | 'weak' | 'none'> =
  {
    'private-link': 'strongest',
    'private-endpoint': 'strongest',
    'vpc-origin': 'strongest',
    'serverless-neg': 'strong',
    'access-restriction': 'strong',
    'allow-list-and-header': 'medium',
    header: 'weak',
    none: 'none',
  };

export function edgeOf(kinds: NetworkKinds | undefined, kind: string): EdgeKind | undefined {
  return kinds?.edges.find((k) => k.kind === kind);
}

/** Whether an add-on is on: the saved `enabled`, else the registry's `default`. */
export function addOnEnabled(ep: Pick<EntryPointSettings, 'addOns'>, kind: EdgeKind | undefined, id: string) {
  const saved = ep.addOns?.[id];
  if (saved) return saved.enabled;
  return kind?.addOns.find((a) => a.id === id)?.default ?? false;
}

/** The WAF's mode when it is on, else null. */
export function wafMode(
  ep: EntryPointSettings,
  kind: EdgeKind | undefined,
): 'prevention' | 'detection' | null {
  if (!kind?.addOns.some((a) => a.id === 'waf') || !addOnEnabled(ep, kind, 'waf')) return null;
  return ep.addOns?.waf?.mode ?? 'prevention';
}

/** "Azure Front Door · Premium", "Azure Application Gateway · WAF_v2 · northeurope hub". */
export function kindLine(ep: EntryPointSettings, kind: EdgeKind | undefined): string {
  const label = kind?.label ?? ep.kind;
  const tier = kind?.tiers.find((t) => t.id === ep.tier)?.label ?? ep.tier ?? null;
  const parts = [label, tier, ep.region ? e.regionHub(ep.region) : null].filter(Boolean);
  return parts.join(' · ');
}

export function tierLabel(ep: EntryPointSettings, kind: EdgeKind | undefined): string | null {
  return kind?.tiers.find((t) => t.id === ep.tier)?.label ?? ep.tier ?? null;
}

export interface AddOnChip {
  id: string;
  text: string;
  tone: 'success' | 'warning' | 'neutral';
  /** Read with the chip (detection mode's consequence). */
  hint?: string;
}

/** The card header's add-on chips (spec 11 6.15.10): WAF with its mode, CDN, private origins, DDoS. */
export function addOnChips(ep: EntryPointSettings, kind: EdgeKind | undefined): AddOnChip[] {
  const chips: AddOnChip[] = [];
  const c = e.chips;
  for (const addOn of kind?.addOns ?? []) {
    const on = addOnEnabled(ep, kind, addOn.id);
    if (addOn.id === 'waf') {
      if (!on) continue;
      const mode = ep.addOns?.waf?.mode ?? 'prevention';
      const managed = ep.addOns?.waf?.managedRules && ep.tier === 'premium';
      chips.push(
        mode === 'detection'
          ? { id: 'waf', text: c.wafDetection, tone: 'warning', hint: c.wafDetectionHint }
          : {
              id: 'waf',
              text: managed ? `${c.wafPrevention} · ${c.managedRules}` : c.wafPrevention,
              tone: 'success',
            },
      );
    } else if (addOn.id === 'cdn') chips.push({ id: 'cdn', text: on ? c.cdnOn : c.cdnOff, tone: 'neutral' });
    else if (on)
      chips.push({
        id: addOn.id,
        text:
          addOn.id === 'ddos-ip-protection'
            ? c.ddosIp
            : addOn.id === 'ddos-network-protection'
              ? c.ddosNetwork
              : addOn.id === 'private-origins'
                ? c.privateOrigins
                : addOn.label,
        tone: 'neutral',
      });
  }
  return chips;
}

/** The modes a route may choose for a platform on this entry point (Private Link needs Premium's private origins). */
export function offeredModes(
  ep: Pick<EntryPointSettings, 'tier' | 'addOns'>,
  kind: EdgeKind | undefined,
  platform: string,
): OriginAccessMode[] {
  const origin = kind?.origins.find((o) => o.platform === platform);
  if (!origin) return [];
  return (origin.access as OriginAccessMode[]).filter(
    (m) => m !== 'private-link' || (ep.tier === 'premium' && addOnEnabled(ep, kind, 'private-origins')),
  );
}

/** What `auto` decides: the strongest offered mode, or null where the registry's default is null (D12). */
export function preselectedMode(
  ep: Pick<EntryPointSettings, 'tier' | 'addOns'>,
  kind: EdgeKind | undefined,
  platform: string,
): OriginAccessMode | null {
  const origin = kind?.origins.find((o) => o.platform === platform);
  if (!origin || origin.default === null) return null;
  const modes = offeredModes(ep, kind, platform);
  return ACCESS_ORDER.find((m) => modes.includes(m)) ?? null;
}

/** The host and path rows of a route ("shop.example.com/api/*"). */
export function hostPaths(
  route: Pick<EntryPointRouteStatus, 'hostnames' | 'paths'>,
  edgeHost: string | null,
) {
  const hosts = route.hostnames.length ? route.hostnames : [edgeHost ?? e.edgeHost];
  const paths = route.paths.length ? route.paths : ['/*'];
  return hosts.flatMap((h) => paths.map((p) => `${h}${p}`));
}

/** The vault's name from a Key Vault secret ID (`https://kv-certs.vault.azure.net/secrets/…` → `kv-certs`). */
export function vaultOf(secretId: string | undefined): string {
  if (!secretId) return 'the vault';
  try {
    return new URL(secretId).hostname.split('.')[0] || secretId;
  } catch {
    return secretId;
  }
}

/** An inactive route's reason in words (6.15.29). */
export function inactiveWords(code: string | null, deployment: string, network: string): string {
  switch (code) {
    case 'ROUTE_ORIGIN_NOT_ATTACHED':
      return e.codes.ROUTE_ORIGIN_NOT_ATTACHED(deployment, network);
    case 'ROUTE_ORIGIN_UNKNOWN':
      return e.codes.ROUTE_ORIGIN_UNKNOWN;
    default:
      return code ?? '';
  }
}
